import { FC, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useBankFeedApi } from '../../../resources/hooks/api/bankFeedApiHook';
import AddBank from '../../../components/Modal/AddBank';

interface BankRow {
    gl_account_id: number;
    gl_account_number: number | null;
    gl_account_label: string;
    qbo_name: string;
    revrex_bank_name?: string;
    revrex_bank_number?: string;
}

interface Props {
    rows: BankRow[];
    branchId: number;
    onCancel: () => void;
    onConfirm: (bankDetails: any[]) => void;
}

export const QBO_BANK_LIST_PAGE_SIZE = 1000;
export const getQboBankListParams = (searchText = '') => ({
    page_size: QBO_BANK_LIST_PAGE_SIZE,
    csv_type_name: 'Bank Deposit',
    with_template: 2,
    network_status: 1,
    search_text: searchText,
});

export const sortQboBankOptions = (options: any[]) => [...options].sort((left: any, right: any) =>
    String(left?.label || '').localeCompare(String(right?.label || ''), undefined, { sensitivity: 'base' })
);

export const buildNewBankRequestFormData = (
    branchId: number,
    bank: { name: string; file: File },
    row: { number: string; desc: string },
) => {
    const payload = new FormData();
    payload.append('branch_id', String(branchId));
    payload.append('name', bank.name);
    payload.append('number', String(row.number));
    payload.append('description', row.desc.trim());
    payload.append('bankRequestFiles[]', bank.file);
    return payload;
};

const QBBankRegistrationModal: FC<Props> = ({ rows, branchId, onCancel, onConfirm }) => {
    const { fetchBankTemplates, requestBank } = useBankFeedApi();
    const { t } = useTranslation("scenes.thirdparty.quickbook.QBCOAClassification");
    const [values, setValues] = useState<any[]>(
        rows.map((row) => {
            const digitMatch = row.qbo_name ? row.qbo_name.match(/\b(\d{4})\b/) : null;
            return {
                network: row.revrex_bank_name ? { label: row.revrex_bank_name, value: row.revrex_bank_name } : null,
                number: row.revrex_bank_number || (digitMatch ? digitMatch[1] : ''),
                desc: row.qbo_name || row.revrex_bank_name || '',
            };
        })
    );
    const [errors, setErrors] = useState<{ network: string; number: string; desc: string }[]>(
        rows.map(() => ({ network: '', number: '', desc: '' }))
    );

    const [bankOptions, setBankOptions] = useState<any[]>([]);
    const [showAddBankModal, setShowAddBankModal] = useState<boolean>(false);
    const [addBankRowIndex, setAddBankRowIndex] = useState<number>(-1);
    const [newBanks, setNewBanks] = useState<Map<number, { name: string; file: File }>>(new Map());
    const [submitting, setSubmitting] = useState<boolean>(false);

    const networkOptions = (searchText: string, rowIndex: number): Promise<any[]> => {
        return new Promise((resolve) => {
            fetchBankTemplates(getQboBankListParams(searchText || ''), (_msg: string, resp: any) => {
                const data = sortQboBankOptions((resp.data.data.rows || []).map((e: any) => ({
                    label: e['name'],
                    value: e['id'],
                    isPending: false
                })));

                if (searchText && searchText.trim() && data.length > 0) {
                    const query = searchText.trim().toLowerCase();
                    const match = data.find((e: any) => e.label.toLowerCase() === query)
                        || data.find((e: any) => e.label.toLowerCase().startsWith(query))
                        || data.find((e: any) => e.label.toLowerCase().includes(query));
                    if (match) {
                        setRow(rowIndex, { network: match });
                        setErrors(prev => prev.map((err, idx) => idx === rowIndex ? { ...err, network: '' } : err));
                    }
                }
                resolve(data);
            }, (msg: string) => {
                toast.error(msg);
                resolve([]);
            });
        });
    };

    const handleInputChange = (rowIndex: number, inputValue: string, actionMeta: any) => {
        if (actionMeta.action !== 'input-change') return;
        const query = inputValue.trim().toLowerCase();
        if (!query) return;

        const match = bankOptions.find((opt: any) => opt.label.toLowerCase() === query)
            || bankOptions.find((opt: any) => opt.label.toLowerCase().startsWith(query))
            || bankOptions.find((opt: any) => opt.label.toLowerCase().includes(query));

        if (match) {
            setRow(rowIndex, { network: match });
            setErrors(prev => prev.map((err, idx) => idx === rowIndex ? { ...err, network: '' } : err));
        }
    };

    const refreshBankOptions = () => {
        fetchBankTemplates(getQboBankListParams(), (_msg: string, resp: any) => {
            const data = sortQboBankOptions((resp.data.data.rows || []).map((e: any) => ({
                label: e['name'],
                value: e['id']
            })));
            setBankOptions(data);
        }, (msg: string) => {
            toast.error(msg);
        });
    };

    useEffect(() => {
        refreshBankOptions();
    }, []);

    const setRow = (idx: number, patch: any) => {
        setValues(prev => prev.map((v, i) => i === idx ? { ...v, ...patch } : v));
        // Errors are only recomputed in handleConfirm, so without this an edited field
        // keeps showing the message from the previous attempt and reads as still-rejected.
        // patch keys match the error keys, so this covers network, number and desc alike.
        const cleared = Object.fromEntries(Object.keys(patch).map(key => [key, '']));
        setErrors(prev => prev.map((e, i) => i === idx ? { ...e, ...cleared } : e));
    };

    const handleOpenAddBankModal = (rowIndex: number) => {
        setAddBankRowIndex(rowIndex);
        setShowAddBankModal(true);
    };

    const handleCloseAddBankModal = () => {
        setShowAddBankModal(false);
        setAddBankRowIndex(-1);
    };

    const handleBankAdded = (created?: { name?: string; file?: File }) => {
        if (created?.name && created?.file && addBankRowIndex >= 0) {
            setNewBanks(previous => new Map(previous).set(addBankRowIndex, {
                name: created.name!,
                file: created.file!,
            }));
            setRow(addBankRowIndex, {
                network: { label: created.name, value: created.name, isPending: true },
            });
        }
        handleCloseAddBankModal();
    };

    const CustomOption = (props: any) => {
        const isPending = props.data.isPending;
        return (
            <components.Option {...props}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>{props.children}</span>
                    {isPending && (
                        <span style={{
                            fontSize: '11px',
                            color: '#fd7e14',
                            marginLeft: '8px',
                            fontWeight: 500
                        }}>
                            {t('badge.pendingApproval')}
                        </span>
                    )}
                </div>
            </components.Option>
        );
    };

    const CustomSingleValue = (props: any) => {
        const isPending = props.data.isPending;
        return (
            <components.SingleValue {...props}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>{props.children}</span>
                    {isPending && (
                        <span style={{
                            fontSize: '11px',
                            color: '#fd7e14',
                            marginLeft: '8px',
                            fontWeight: 500
                        }}>
                            {t('badge.pendingApproval')}
                        </span>
                    )}
                </div>
            </components.SingleValue>
        );
    };

    const CreateBankMenuList = (rowIndex: number) => (props: any) => (
        <components.MenuList {...props}>
            {props.children}
            <div
                style={{ padding: '8px 12px', borderTop: '1px solid #e0e0e0', cursor: 'pointer', color: '#0d6efd', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenAddBankModal(rowIndex);
                }}
            >
                <i className="fa fa-plus-circle" aria-hidden="true"></i>&nbsp;
                {t('page.addNewBank')}
            </div>
        </components.MenuList>
    );

    const handleConfirm = async () => {
        if (submitting) return;
        const newErrors = values.map((v) => ({
            network: !v.network ? t('validation.network') : '',
            number: !/^\d{4}$/.test(String(v.number)) ? t('validation.number') : '',
            desc: !v.desc || v.desc.trim().length < 4 ? t('validation.desc') : '',
        }));
        setErrors(newErrors);
        if (newErrors.some(e => e.network || e.number || e.desc)) return;

        setSubmitting(true);
        try {
            await Promise.all(Array.from(newBanks.entries()).map(([rowIndex, bank]) => (
                new Promise<void>((resolve, reject) => {
                    const payload = buildNewBankRequestFormData(branchId, bank, values[rowIndex]);
                    requestBank(payload, (message: string) => {
                        toast.success(message || t('toast.bankSubmitted', { name: bank.name }));
                        resolve();
                    }, (message: string) => {
                        toast.error(message);
                        reject(new Error(message));
                    });
                })
            )));
        } catch (_error) {
            setSubmitting(false);
            return;
        }

        const payload = rows.map((r, i) => ({
            gl_account_id: r.gl_account_id,
            gl_account_number: r.gl_account_number,
            network_label: values[i].network.label,
            network_id: values[i].network.value,
            number: values[i].number,
            desc: values[i].desc,
        }));
        onConfirm(payload);
    };

    return (
        <>
        <Modal show={true} onHide={onCancel} backdrop="static" animation={false} size="xl" centered>
            <Modal.Header>
                <Modal.Title>{t('modal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted">
                    {t('modal.description')}
                </p>
                <div className="table-responsive">
                    <table className="table table-default align-middle">
                        <thead>
                            <tr>
                                <th>{t('table.headers.qboAccount')}</th>
                                <th>{t('table.headers.mappedGlAccount')}</th>
                                <th style={{ minWidth: 220 }}>{t('table.headers.revrexBank')}<span className="text-danger">*</span></th>
                                <th style={{ width: 140, minWidth: 140 }}>{t('table.headers.last4')}<span className="text-danger">*</span></th>
                                <th style={{ minWidth: 200 }}>{t('table.headers.description')}<span className="text-danger">*</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.gl_account_id}>
                                    <td><strong>{r.qbo_name}</strong></td>
                                    <td>{r.gl_account_label}</td>
                                    <td>
                                        <AsyncSelect
                                            placeholder={t('placeholder.selectBank')}
                                            isClearable
                                            defaultOptions={bankOptions}
                                            loadOptions={(txt: string) => networkOptions(txt, i)}
                                            onInputChange={(val: string, meta: any) => handleInputChange(i, val, meta)}
                                            menuPosition="fixed"
                                            value={values[i].network}
                                            onChange={(opt: any) => setRow(i, { network: opt })}
                                            components={{
                                                MenuList: CreateBankMenuList(i),
                                                Option: CustomOption,
                                                SingleValue: CustomSingleValue
                                            }}
                                        />
                                        {errors[i].network && (
                                            <small className="text-danger">{errors[i].network}</small>
                                        )}
                                    </td>
                                    <td style={{ minWidth: 140 }}>
                                        <Form.Control
                                            type="text"
                                            maxLength={4}
                                            value={values[i].number}
                                            onChange={(e: any) => setRow(i, { number: e.target.value.replace(/\D/g, '') })}
                                            placeholder="1234"
                                            style={{ width: '100%', minWidth: '120px', textAlign: 'center', letterSpacing: '1px', padding: '6px 8px' }}
                                        />
                                        {errors[i].number && (
                                            <small className="text-danger">{errors[i].number}</small>
                                        )}
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            value={values[i].desc}
                                            onChange={(e: any) => setRow(i, { desc: e.target.value })}
                                            placeholder={t('placeholder.description')}
                                        />
                                        {errors[i].desc && (
                                            <small className="text-danger">{errors[i].desc}</small>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-cancel" disabled={submitting} onClick={onCancel}>{t('buttons.cancel')}</button>
                <button type="button" className="btn btn-primary" disabled={submitting} onClick={handleConfirm}>
                    {submitting ? t('buttons.saving', { defaultValue: 'Saving...' }) : t('buttons.confirmSave')}
                </button>
            </Modal.Footer>
        </Modal>
        {showAddBankModal && (
            <AddBank
                isShow={showAddBankModal}
                onHide={handleCloseAddBankModal}
                onSuccess={handleBankAdded}
                initialName={rows[addBankRowIndex]?.qbo_name || ''}
            />
        )}
        </>
    );
};

export default QBBankRegistrationModal;
